import React from "react";
import PropTypes from "prop-types";
import AutoComplete from "@oxygen/core/lib/Autocomplete/Autocomplete";
import { autocomplete } from "GlobalUtils/Autocomplete";
import { CITY_AUTOCOMPLETE_URL } from "GlobalConstant/index";
import { debounce } from "throttle-debounce";
import { makeCancelable } from "GlobalUtils/CancelablePromise";
import {
  getUsedCarBudgetUrls,
  getUsedCarSearchPagePath,
} from "GlobalUtils/UrlFactory";
import getThemeClasses from "@oxygen/theme";
import { CSSFlex } from "GlobalComponent/CSSCore";
import styles from "./scss/UsedCarsSearchWidget.module.scss";
import { ChevronRight } from "@oxygen/icons";
import { platform as PLATFORM } from "GlobalEnum/Platform";
import { Button } from "@oxygen/core";
import searchStyles from "./scss/NewCarsSearchWidget.module.scss";
import PriceRangeSelection from "SrcPath/used/Landing/components/PriceRangeSelection";
import { BUDGET_RANGE_LIMITS } from "SrcPath/used/Landing/constants";
import { Track } from "GlobalUtils/Track";
import { getSearchClickTrackObject } from "SrcPath/Home/utils";
import { setUsedCarSearchInitialState } from "SrcPath/Home/mapper";
import {
  getLowerBudgetValue,
  getUpperBudgetValue,
} from "SrcPath/used/Landing/utils/UsedLandingUtils";

const propTypes = {
  globalLocation: PropTypes.object.isRequired,
};

const autocompleteContainerClasses = getThemeClasses(`
  width-full;
  height-11;
  flex-grow-1;
`);
const searchButtonClasses = getThemeClasses(`
  mr-0.5;
`);
const mainContainerClasses = getThemeClasses(`
  flex-grow-1;
  height-11;
`);
const iconContainerClasses = getThemeClasses(`
  justify-content-center;
  align-items-center;
  bg-red;
  color-white;
  border-top-right-radius-3;
  border-bottom-right-radius-3;
  width-11;
  height-11;
  ${styles.rightArrowContainer}
`);
const autocompleteClasses = getThemeClasses(`
  z-index-2;
`);
const buttonContainerClasses = getThemeClasses(`
  vertical-align-middle;
`);
const budgetSelectionWrapperClasses = getThemeClasses(`
  position-relative;
`);
const budgetClasses = getThemeClasses(`
  height-full;
  pl-4;
  pr-4;
  pt-3;
  pb-3;
  border-left-style-solid;
  border-left-width-1;
  border-color-gainsBoroGray;
  width-auto;
  font-size-7.5;
  color-darkGray;
  text-align-center;
`);
const getPriceRangeText = (lowerBudgetLimit, upperBudgetLimit) => {
  const lowerLimit =
    Number(lowerBudgetLimit) < 0 ? BUDGET_RANGE_LIMITS.min : lowerBudgetLimit;
  const upperLimit =
    Number(upperBudgetLimit) < 0 ? BUDGET_RANGE_LIMITS.max : upperBudgetLimit;
  return `${lowerLimit} - ${upperLimit} Lakh`;
};
const priceRangeClass = getThemeClasses(`
  position-absolute;
  z-index-2;
`);
const inputPlaceHolder = "City, eg: Mumbai";

const SearchButton = ({ platform, onClick }) => {
  if (platform.id === PLATFORM.MOBILE.id) {
    return (
      <CSSFlex className={iconContainerClasses} onClick={onClick}>
        <ChevronRight />
      </CSSFlex>
    );
  }
  return (
    <div className={buttonContainerClasses}>
      <Button
        className={searchButtonClasses}
        paddingXClassName={searchStyles.searchButtonPadding}
        onClick={onClick}
      >
        Search
      </Button>
    </div>
  );
};

export default class UsedCarsSearchWidget extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...setUsedCarSearchInitialState(props),
    };
  }
  processResults = results => {
    console.log("results", results);
    if (results) {
      const autoCompleteResult = results
        .filter(
          cityResult =>
            cityResult &&
            cityResult.result &&
            cityResult.payload &&
            cityResult.payload.cityId > 0
        )
        .map(cityResult => ({
          label: cityResult.result,
          cityid: cityResult.payload.cityId,
          cityMaskingName: cityResult.payload.cityMaskingName,
        }));
      this.setState({ results: autoCompleteResult });
    }
  };
  stopEventPropagation = event => {
    event.stopPropagation();
  };
  handleBudgetLimit = (lowerBudgetLimit, upperBudgetLimit) => {
    this.setState({
      lowerBudgetLimit,
      upperBudgetLimit,
      priceRangeText: getPriceRangeText(lowerBudgetLimit, upperBudgetLimit),
    });
  };

  handleLowerBudgetValidation = isValidPrice => {
    this.setState({
      lowerBudgetValidation: isValidPrice,
    });
  };

  handleUpperBudgetValidation = isValidPrice => {
    this.setState({
      upperBudgetValidation: isValidPrice,
    });
  };

  handleAutoCompleteInputChange = event => {
    const value = event.target.value.trimLeft();
    this.setState({
      text: value,
    });
    this.apiPromise && this.apiPromise.cancel();
    if (value) {
      this.fetchResultsAndUpdateState(value);
    } else {
      this.setState({
        results: [],
      });
    }
  };

  fetchResultsAndUpdateState = input => {
    this.apiPromise = makeCancelable(
      autocomplete(
        {
          url: CITY_AUTOCOMPLETE_URL,
          source: this.props.platform.id,
          resultCount: 5,
        },
        input
      )
    );
    this.apiPromise.promise
      .then(this.processResults)
      .catch(this.handleApiFailure);
  };

  handleApiFailure = error => {
    console.log("city autocomplete api failed", error);
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.globalLocation.cityId > 0 &&
      this.props.globalLocation.cityName &&
      prevProps.globalLocation.cityId !== this.props.globalLocation.cityId
    ) {
      this.setState({
        text: this.props.globalLocation.cityName,
        results: [],
        cityId: this.props.globalLocation.cityId,
        isPriceRangeSelectorVisible: true,
      });
    }
  }

  componentDidMount() {
    this.fetchResultsAndUpdateState = debounce(
      200,
      this.fetchResultsAndUpdateState
    );
    window.addEventListener("click", this.handleWindowClick);
    window.addEventListener("click", this.disablePriceRangeSelector);
  }

  componentWillUnmount() {
    window.removeEventListener("click", this.handleWindowClick);
    window.removeEventListener("click", this.disablePriceRangeSelector);
  }

  handleWindowClick = () => {
    if (this.state.results.length) {
      this.setState({
        results: [],
      });
    }
  };

  handlePriceRangeSelectorVisibility = event => {
    event.stopPropagation();
    this.setState(prevState => ({
      isPriceRangeSelectorVisible: !prevState.isPriceRangeSelectorVisible,
    }));
  };
  disablePriceRangeSelector = event => {
    const { isPriceRangeSelectorVisible } = this.state;
    if (isPriceRangeSelectorVisible) {
      this.setState(prevState => ({
        isPriceRangeSelectorVisible: !prevState.isPriceRangeSelectorVisible,
      }));
    }
  };

  handleResultsClick = event => {
    const { results } = this.state;
    const { platform } = this.props;
    const city =
      event.currentTarget && event.currentTarget.getAttribute("data-cityid");
    const cityId = parseInt(city);
    if (cityId < 1) {
      return;
    }
    const cityDetails = results.find(element => element.cityid === cityId);
    this.setState({
      text: cityDetails.label.split(",")[0],
      cityMaskingName: cityDetails.cityMaskingName,
      results: [],
      isPriceRangeSelectorVisible: true,
    });
    if (cityId > 0 && platform.id === PLATFORM.MOBILE.id) {
      window.location.href = getUsedCarSearchPagePath({ cityId });
    }
  };

  handleSearchClick = event => {
    const {
      lowerBudgetValidation,
      upperBudgetValidation,
      lowerBudgetLimit,
      upperBudgetLimit,
      cityMaskingName,
      cityId,
    } = this.state;
    const { platform } = this.props;
    if (cityId > 0 && platform.id === PLATFORM.MOBILE.id) {
      window.location.href = getUsedCarSearchPagePath({ cityId });
    }
    if (lowerBudgetValidation && upperBudgetValidation) {
      const lowerBudgetLimitValue = getLowerBudgetValue(lowerBudgetLimit);
      const upperBudgetLimitValue = getUpperBudgetValue(upperBudgetLimit);

      window.location.href = getUsedCarBudgetUrls({
        cityMaskingName,
        minPrice: lowerBudgetLimitValue * 100000, //multiplying by 1Lakh to get the correct url
        maxPrice: upperBudgetLimitValue * 100000, //multiplying by 1Lakh to get the correct url
      });
    } else {
      event.stopPropagation();
      this.setState({
        isPriceRangeSelectorVisible: true,
      });
    }
  };
  render() {
    const {
      text,
      results,
      priceRangeText,
      lowerBudgetLimit,
      upperBudgetLimit,
      cityId,
      cityMaskingName,
      isPriceRangeSelectorVisible,
    } = this.state;
    const { platform } = this.props;
    const trackObj = getSearchClickTrackObject(
      cityId,
      cityMaskingName,
      lowerBudgetLimit,
      upperBudgetLimit,
      platform
    );
    const upperBudget =
      parseInt(upperBudgetLimit) > BUDGET_RANGE_LIMITS.max
        ? BUDGET_RANGE_LIMITS.max
        : upperBudgetLimit;
    const lowerBudget =
      parseInt(lowerBudgetLimit) < BUDGET_RANGE_LIMITS.min
        ? BUDGET_RANGE_LIMITS.min
        : lowerBudgetLimit;
    return (
      <>
        <CSSFlex className={mainContainerClasses}>
          <div
            className={`${autocompleteContainerClasses} ${styles.autocompleteContainer}`}
          >
            <AutoComplete
              data-testing-id="used-car-search"
              onChange={this.handleAutoCompleteInputChange}
              inputIcon="search"
              labelKey="label"
              results={results}
              placeholder={inputPlaceHolder}
              clearButton={false}
              isMenuShown={results.length > 0}
              text={text}
              onClick={this.handleResultsClick}
              className={autocompleteClasses}
            />
          </div>
          {platform.id === PLATFORM.DESKTOP.id && (
            <div
              className={`${budgetSelectionWrapperClasses} ${styles.budgetWrapper}`}
            >
              {isPriceRangeSelectorVisible && (
                <div
                  className={`${priceRangeClass} ${styles.priceRangeWrapper}`}
                  onClick={this.stopEventPropagation}
                >
                  <PriceRangeSelection
                    handleBudgetLimit={this.handleBudgetLimit}
                    lowerBudget={lowerBudget}
                    upperBudget={upperBudget}
                    handleLowerBudgetValidation={
                      this.handleLowerBudgetValidation
                    }
                    handleUpperBudgetValidation={
                      this.handleUpperBudgetValidation
                    }
                    platform={platform}
                  />
                </div>
              )}
              <div
                className={budgetClasses}
                onClick={this.handlePriceRangeSelectorVisibility}
              >
                {priceRangeText}
              </div>
            </div>
          )}
        </CSSFlex>
        <Track trackClick {...trackObj}>
          <SearchButton platform={platform} onClick={this.handleSearchClick} />
        </Track>
      </>
    );
  }
}

UsedCarsSearchWidget.propTypes = propTypes;
